import { Loading } from "@admin/components/atoms/Loading";
import { useCompany } from "@admin/hooks/useCompany";
import useFoodManagementModals from "@admin/hooks/useFoodManagementModals";
import { useFoodReports } from "@admin/hooks/useFoodReport";
import { useLocationsWithLicenses } from "@admin/hooks/useLocationsWithLicenses";
import {
  categorizeFoodReports,
  dateToISO,
  FoodControlPointInstance,
  FoodReport,
  getAllDefectLogsByLocationId,
  getAllFoodDrawalsByLocationId,
  getAllFoodPoisoningsByLocationId,
  InstanceStatus,
  LicenseTypes,
  useCommonStore,
} from "@enos-monorepo/lib";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ControlPointModals } from "../control-points/components";
import {
  InfoModal,
  LogTable,
  ReportLocations,
  ReportTable,
} from "./components";

interface CurrentInstance {
  instances: FoodControlPointInstance[];
  report: FoodReport;
  date: Date;
  isOnAddMoreClick?: boolean;
}

export function FoodManagementReportPage() {
  const { t } = useTranslation();
  const { company } = useCompany();
  const { locations } = useLocationsWithLicenses(LicenseTypes.food);
  const { openModal, closeModals, modalOpen } = useFoodManagementModals();
  const [selectedMonth, setSelectedMonth] = useState<Date>();
  const [infoModalVisible, setInfoModalVisible] = useState(false);
  const [selectedReport, setSelectedReport] = useState<{
    report: FoodReport;
    instances: FoodControlPointInstance[];
    date: Date;
  }>();
  const { foodLocation } = useCommonStore();
  const currentLocation = useMemo(() => {
    if (foodLocation && locations) {
      return locations.find((l) => l.id === foodLocation) || locations[0];
    }
    return locations && locations.length > 0 ? locations[0] : undefined;
  }, [foodLocation, locations]);
  const [currentInstance, setCurrentInstance] =
    useState<FoodControlPointInstance>();

  const { data: locationFoodDrawals } = useQuery({
    queryKey: ["locationFoodDrawals", currentLocation?.id],
    queryFn: () => getAllFoodDrawalsByLocationId(currentLocation?.id ?? ""),
    enabled: !!currentLocation,
  });

  const { data: locationDefectLogs } = useQuery({
    queryKey: ["locationDefectLogs", currentLocation?.id],
    queryFn: () => getAllDefectLogsByLocationId(currentLocation?.id ?? ""),
    enabled: !!currentLocation,
  });

  const { data: locationFoodPoisonings } = useQuery({
    queryKey: ["locationFoodPoisonings", currentLocation?.id],
    queryFn: () => getAllFoodPoisoningsByLocationId(currentLocation?.id ?? ""),
    enabled: !!currentLocation,
  });

  const report = useFoodReports(currentLocation?.id ?? "");

  const categorized = categorizeFoodReports(report.foodReports);

  const handleCellClick = useCallback(
    (params: CurrentInstance) => {
      if (params.instances?.length > 0 && !params.isOnAddMoreClick) {
        setSelectedReport(params);
        setInfoModalVisible(true);
      } else {
        if (company && currentLocation) {
          setInfoModalVisible(false);
          setCurrentInstance({
            foodControlPointId: params.report.id,
            // 1 week before params.date
            startDate: dateToISO(
              new Date(params.date.getTime() - 7 * 24 * 60 * 60 * 1000),
            ),
            endDate: dateToISO(params.date),
            updatedDate: dateToISO(params.date),
            status: InstanceStatus.empty,
            companyId: company.companyId,
            companyLocationId: currentLocation.id,
          });
          openModal(params.report.type);
        }
      }
    },
    [company, currentLocation, openModal],
  );

  if (report.isPending) {
    return <Loading />;
  }

  return (
    <div className="flex flex-col gap-7.5 max-w-7xl">
      <ReportLocations
        setSelectedMonth={setSelectedMonth}
        selectedLocation={currentLocation}
        locations={locations}
      />
      <ReportTable
        title={t("food.Inventory")}
        report={categorized["Inventory Controls"]?.sort((a, b) =>
          (a.name ?? "").localeCompare(b.name ?? ""),
        )}
        selectedMonth={selectedMonth}
        onCellClick={handleCellClick}
      />
      <ReportTable
        title={t("food.Dishwasher Controls")}
        report={categorized["Dishwasher Controls"]}
        selectedMonth={selectedMonth}
        onCellClick={handleCellClick}
      />
      <ReportTable
        title={t("food.Food Acceptance")}
        report={categorized["Food Acceptance Controls"]}
        selectedMonth={selectedMonth}
        onCellClick={handleCellClick}
      />
      <ReportTable
        title={t("food.Food Processing")}
        report={categorized["Food Processing Controls"]}
        selectedMonth={selectedMonth}
        onCellClick={handleCellClick}
      />
      <ReportTable
        title={t("food.Cleaning")}
        report={categorized["Cleaning Controls"]}
        selectedMonth={selectedMonth}
        onCellClick={handleCellClick}
      />
      <ReportTable
        title={t("food.Yearly Others")}
        report={categorized["Yearly or Other Controls"]}
        selectedMonth={selectedMonth}
        onCellClick={handleCellClick}
      />
      <LogTable
        title={t("food.Log Book")}
        companyId={company?.companyId ?? ""}
        selectedLocationId={currentLocation?.id ?? ""}
        selectedMonth={selectedMonth}
        report={{
          defects: locationDefectLogs ?? [],
          drawals: locationFoodDrawals ?? [],
          poisonings: locationFoodPoisonings ?? [],
        }}
      />
      <InfoModal
        open={infoModalVisible}
        onClose={() => setInfoModalVisible(false)}
        selectedReport={selectedReport}
        onAddMoreClick={() => {
          handleCellClick({
            ...selectedReport,
            isOnAddMoreClick: true,
          } as CurrentInstance);
        }}
      />
      <ControlPointModals
        closeModals={closeModals}
        modalOpen={modalOpen}
        currentInstance={currentInstance}
      />
    </div>
  );
}
